import { type CSSProperties } from "react";
import { NotchedInput, KeycapButton } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

const Mail = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

/**
 * Verification harness for NotchedInput — a field in the hex-notch silhouette.
 * Verify in a real browser (shape() clip): the keyline ring traces the notch
 * (no border slice), goes accent on focus + destructive on aria-invalid, and the
 * field pairs with KeycapButton as a coherent notched form.
 */
export function NotchedInputDemo() {
  return (
    <Section
      id="notched-input"
      title="NotchedInput"
      description="A text field in the hex-notch silhouette — the form companion to KeycapButton. The outline is an nx-rim ring (a real border is sliced at the notch): hairline at rest, accent on focus, destructive on aria-invalid, plus an accent drop-shadow glow on focus. Sizes sm/default/lg, optional leadingIcon. ref forwards to the <input> for form libraries."
    >
      <VariantBlock
        label="States — rest · focus · invalid"
        caption="the keyline ring is the focus affordance"
      >
        <div className="flex max-w-md flex-col gap-3">
          <NotchedInput placeholder="Rest — hairline keyline" leadingIcon={<Mail />} />
          <NotchedInput placeholder="Focused (autofocus)" leadingIcon={<Mail />} autoFocus />
          <NotchedInput
            placeholder="Invalid — destructive keyline"
            aria-invalid
            defaultValue="not-an-email"
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Size — sm · default · lg" caption="height + type scale">
        <div className="flex max-w-md flex-col gap-3">
          <NotchedInput size="sm" placeholder="Small" />
          <NotchedInput size="default" placeholder="Default" />
          <NotchedInput size="lg" placeholder="Large" />
        </div>
      </VariantBlock>

      <VariantBlock label="A notched form — input + KeycapButton" caption="the primitives compose">
        <div
          className="flex max-w-md items-center gap-3"
          style={{ "--primary": "238 79% 64%" } as CSSProperties}
        >
          <NotchedInput className="" placeholder="name@team.com" leadingIcon={<Mail />} />
          <KeycapButton variant="primary">Invite</KeycapButton>
        </div>
      </VariantBlock>
    </Section>
  );
}
